import { useState } from 'react';
import { MapPin, CheckCircle2, AlertTriangle, Loader2 } from 'lucide-react';

type Status = 'idle' | 'testing' | 'success' | 'error';

export const LocationSettings = () => {
  const [status, setStatus] = useState<Status>('idle');
  const [accuracy, setAccuracy] = useState<number | null>(null);

  const runTest = () => {
    if (!navigator.geolocation) {
      setStatus('error');
      return;
    }
    setStatus('testing');
    navigator.geolocation.getCurrentPosition(
      (position) => {
        setAccuracy(Math.round(position.coords.accuracy));
        setStatus('success');
      },
      () => setStatus('error'),
      { enableHighAccuracy: true, timeout: 10000, maximumAge: 30000 },
    );
  };

  return (
    <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-5">
      <div className="flex items-start gap-3">
        <MapPin className="w-5 h-5 text-gray-500 dark:text-gray-400 mt-0.5 flex-shrink-0" />
        <div className="flex-1 min-w-0">
          <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100">Location</h3>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
            In-person visits capture your location so managers can confirm school visits. Test that
            your phone is sharing location with the CRM.
          </p>

          {status === 'success' && (
            <p className="mt-3 inline-flex items-center gap-1.5 text-sm font-medium text-green-700 dark:text-green-400">
              <CheckCircle2 className="w-4 h-4" />
              Working{accuracy != null ? ` (±${accuracy} m)` : ''}
            </p>
          )}

          {status === 'error' && (
            <div className="mt-3 text-sm text-gray-700 dark:text-gray-300">
              <p className="inline-flex items-center gap-1.5 font-medium text-amber-700 dark:text-amber-400">
                <AlertTriangle className="w-4 h-4" />
                Couldn't get your location
              </p>
              <p className="mt-2">On iPhone, turn it on in:</p>
              <p className="mt-1 font-mono text-xs bg-gray-50 dark:bg-gray-900 rounded px-2 py-1">
                Settings → Privacy &amp; Security → Location Services → (on) → Safari Websites → "While
                Using the App," Precise Location on
              </p>
              <p className="mt-2 text-gray-500 dark:text-gray-400">
                Then come back and re-test.
              </p>
            </div>
          )}

          <button
            type="button"
            onClick={runTest}
            disabled={status === 'testing'}
            className="mt-4 inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-blast-600 hover:bg-blast-700 text-white text-sm font-medium disabled:opacity-60"
          >
            {status === 'testing' && <Loader2 className="w-4 h-4 animate-spin" />}
            {status === 'error' ? 'Re-test' : 'Test my location'}
          </button>
        </div>
      </div>
    </div>
  );
};
